import {
  BrowserRouter,
  Link,
  MemoryRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import type { ReactNode } from "react";
import type { FrameworkSnapshot } from "@apf/framework-config";
import type { FrameworkDataProvider } from "@apf/workblock-adapter";
import { domainWorkblockPath } from "./catalogue";
import { FrameworkProvider, useCopy, useFramework } from "./framework";
import { AppShell } from "./components/AppShell";
import { AbsenceBlock } from "./components/ui";
import { FrameworkPage } from "./pages/FrameworkPages";
import { GroundworksPage } from "./pages/GroundworksPage";
import { PublishedWorkblocksPage } from "./pages/PublishedWorkblocksPage";
import {
  ArchitecturePage,
  FaqsPage,
  ReferencePage,
  ReleasePage,
} from "./pages/ReferencePages";
import { LanguagePage } from "./pages/LanguagePage";
import { DownloadsPage } from "./pages/DownloadsPage";
import { SearchPage } from "./pages/SearchPage";
import {
  OfficeCapabilitiesPage,
  OfficeProductPage,
} from "./pages/OfficeProductPage";
import { WhyGroundworksPage } from "./pages/WhyGroundworksPage";
import { BuildPage, ClientEngagementPage } from "./pages/SectionLandingPages";
import { EditorialStoryPage } from "./pages/EditorialStoryPage";
import { ConnectedWorkPage } from "./pages/ConnectedWorkPage";
import {
  IntegrationExampleRecordPage,
  IntegrationMappingRecordPage,
  IntegrationRecipeRecordPage,
  IntegrationStandardRecordPage,
  IntegrationSystemRecordPage,
  IntegrationToolRecordPage
} from "./pages/ConnectedWorkRecordPages";
import { CapabilityMapPage } from "./pages/CapabilityMapPage";
import {
  JobsReferencePage,
  RoleDetailPage,
  RolesReferencePage,
} from "./pages/RoleJobReferencePages";
import {
  WorkMomentDetailPage,
  WorkMomentsReferencePage,
} from "./pages/WorkMomentsReferencePage";
import { DesignPage } from "./pages/DesignPage";
import { DataModelPage } from "./pages/DataModelPage";
import { AuthoringGuidePage } from "./pages/AuthoringGuidePage";
import {
  groundworksPublicMountMetaName,
  resolveGroundworksBasename,
} from "./routerMount";
import { isProjectedRouteAllowed } from "./routeProjection";

function PageNotFound() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const businessModuleCount = snapshot.content.domainWorkblocks.reduce(
    (count, workblock) => count + workblock.businessModuleIds.length,
    0,
  );
  return (
    <div className="apf-page">
      <AbsenceBlock
        eyebrow={copy("absence.address.eyebrow", "Framework address")}
        title={copy("absence.address.title", "Page not found")}
        support={copy("absence.address.support", "There is no framework page at this address.")}
        headingLevel={1}
        holds={[
          { label: copy("absence.holding.workblocks", "Domain Workblocks"), count: snapshot.content.domainWorkblocks.length },
          { label: copy("absence.holding.business-modules", "Business modules"), count: businessModuleCount },
          { label: copy("absence.holding.entities", "Shared entities"), count: snapshot.groundworks.entities.length },
        ]}
        routes={
          <>
            <Link to="/">{copy("absence.address.route-home", "Go to the framework")}</Link>
            <Link to="/search">{copy("absence.address.route-search", "Search GroundWorks")}</Link>
          </>
        }
      />
    </div>
  );
}

function ProjectedRoute({
  parentPath,
  children,
}: {
  parentPath: string;
  children: ReactNode;
}) {
  const { snapshot } = useFramework();
  return isProjectedRouteAllowed(snapshot, parentPath) ? (
    children
  ) : (
    <PageNotFound />
  );
}

/*
 * One declaration serves every published editorial story. The story id is the
 * path segment, the page resolves the story record from the projected
 * editorial stories by that id, and the projected-route allowlist still gates
 * the concrete path — the same gate, on the same path, the per-story
 * declarations carried. An id with no projected route falls to the shared
 * not-found page; an id with a route but no story record meets the page's own
 * absence state.
 */
function EditorialStoryRoute() {
  const { storyId = "" } = useParams();
  return (
    <ProjectedRoute parentPath={`/client-engagement/${storyId}`}>
      <EditorialStoryPage storyId={storyId} />
    </ProjectedRoute>
  );
}

function MovedTo({
  path,
  defaultAnchor,
}: {
  path: string;
  defaultAnchor: string;
}) {
  const location = useLocation();
  return (
    <Navigate to={`${path}${location.hash || `#${defaultAnchor}`}`} replace />
  );
}

/** The retired marketing domain tree resolves to the canonical Workblock record. */
function DomainMovedToWorkblock() {
  const { domainWorkblockId = "" } = useParams();
  return <Navigate to={domainWorkblockPath(domainWorkblockId)} replace />;
}

/** The published Accenture GroundWorks site. Internal enablement routes are not imported. */
export function SiteRoutes() {
  return (
    <AppShell>
      <Routes>
        <Route
          path="/"
          element={
            <ProjectedRoute parentPath="/">
              <FrameworkPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/framework"
          element={
            <ProjectedRoute parentPath="/">
              <Navigate to="/" replace />
            </ProjectedRoute>
          }
        />
        <Route
          path="/client-engagement"
          element={
            <ProjectedRoute parentPath="/client-engagement">
              <ClientEngagementPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/client-engagement/:storyId"
          element={<EditorialStoryRoute />}
        />
        <Route
          path="/build"
          element={
            <ProjectedRoute parentPath="/build">
              <BuildPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/build/workblock-design"
          element={
            <ProjectedRoute parentPath="/build/workblock-design">
              <AuthoringGuidePage guideId="workblock-design" />
            </ProjectedRoute>
          }
        />
        <Route
          path="/build/workblock-architecture"
          element={
            <ProjectedRoute parentPath="/build/workblock-architecture">
              <AuthoringGuidePage guideId="workblock-architecture" />
            </ProjectedRoute>
          }
        />
        <Route
          path="/domains"
          element={
            <ProjectedRoute parentPath="/workblocks">
              <Navigate to="/workblocks" replace />
            </ProjectedRoute>
          }
        />
        <Route
          path="/domains/:domainWorkblockId"
          element={
            <ProjectedRoute parentPath="/workblocks">
              <DomainMovedToWorkblock />
            </ProjectedRoute>
          }
        />
        <Route
          path="/explainer"
          element={
            <ProjectedRoute parentPath="/build">
              <MovedTo path="/build" defaultAnchor="delivery-motion" />
            </ProjectedRoute>
          }
        />
        <Route
          path="/groundworks/explainer"
          element={<Navigate to="/build" replace />}
        />
        <Route
          path="/capability-map"
          element={
            <ProjectedRoute parentPath="/capability-map">
              <CapabilityMapPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/why-groundworks"
          element={
            <ProjectedRoute parentPath="/why-groundworks">
              <WhyGroundworksPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/design"
          element={
            <ProjectedRoute parentPath="/design">
              <DesignPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/connected-work"
          element={
            <ProjectedRoute parentPath="/connected-work">
              <ConnectedWorkPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/connected-work/recipes/:recipeId"
          element={
            <ProjectedRoute parentPath="/connected-work">
              <IntegrationRecipeRecordPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/connected-work/examples/:exampleId"
          element={
            <ProjectedRoute parentPath="/connected-work">
              <IntegrationExampleRecordPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/connected-work/tools/:toolId"
          element={
            <ProjectedRoute parentPath="/connected-work">
              <IntegrationToolRecordPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/connected-work/systems/:systemId"
          element={
            <ProjectedRoute parentPath="/connected-work">
              <IntegrationSystemRecordPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/connected-work/mappings/:mappingId"
          element={
            <ProjectedRoute parentPath="/connected-work">
              <IntegrationMappingRecordPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/connected-work/standards/:formatId"
          element={
            <ProjectedRoute parentPath="/connected-work">
              <IntegrationStandardRecordPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/workblocks"
          element={
            <ProjectedRoute parentPath="/workblocks">
              <PublishedWorkblocksPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/workblocks/:domainWorkblockId/business-modules/:businessModuleId"
          element={
            <ProjectedRoute parentPath="/workblocks">
              <PublishedWorkblocksPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/workblocks/:domainWorkblockId"
          element={
            <ProjectedRoute parentPath="/workblocks">
              <PublishedWorkblocksPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/data-model"
          element={
            <ProjectedRoute parentPath="/data-model">
              <DataModelPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/groundworks"
          element={
            <ProjectedRoute parentPath="/groundworks">
              <GroundworksPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/groundworks/:entityId"
          element={
            <ProjectedRoute parentPath="/groundworks">
              <GroundworksPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/architecture"
          element={
            <ProjectedRoute parentPath="/architecture">
              <ArchitecturePage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/capabilities"
          element={
            <ProjectedRoute parentPath="/capabilities">
              <OfficeCapabilitiesPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/capabilities/:productId"
          element={
            <ProjectedRoute parentPath="/capabilities">
              <OfficeProductPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/landscape"
          element={
            <ProjectedRoute parentPath="/architecture">
              <MovedTo path="/architecture" defaultAnchor="runs-alongside" />
            </ProjectedRoute>
          }
        />
        <Route
          path="/reference"
          element={
            <ProjectedRoute parentPath="/reference">
              <ReferencePage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/reference/roles/:roleId"
          element={
            <ProjectedRoute parentPath="/reference/roles">
              <RoleDetailPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/reference/roles"
          element={
            <ProjectedRoute parentPath="/reference/roles">
              <RolesReferencePage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/reference/jobs"
          element={
            <ProjectedRoute parentPath="/reference/jobs">
              <JobsReferencePage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/reference/work-moments/:momentId"
          element={
            <ProjectedRoute parentPath="/reference/work-moments">
              <WorkMomentDetailPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/reference/work-moments"
          element={
            <ProjectedRoute parentPath="/reference/work-moments">
              <WorkMomentsReferencePage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/glossary"
          element={
            <ProjectedRoute parentPath="/language">
              <MovedTo path="/language" defaultAnchor="glossary" />
            </ProjectedRoute>
          }
        />
        <Route
          path="/language"
          element={
            <ProjectedRoute parentPath="/language">
              <LanguagePage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/faqs"
          element={
            <ProjectedRoute parentPath="/faqs">
              <FaqsPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProjectedRoute parentPath="/search">
              <SearchPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/release"
          element={
            <ProjectedRoute parentPath="/release">
              <ReleasePage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/downloads"
          element={
            <ProjectedRoute parentPath="/downloads">
              <DownloadsPage />
            </ProjectedRoute>
          }
        />
        <Route
          path="/downloads/:assetId"
          element={
            <ProjectedRoute parentPath="/downloads">
              <DownloadsPage />
            </ProjectedRoute>
          }
        />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </AppShell>
  );
}

export interface SiteAppProps {
  provider?: FrameworkDataProvider;
  initialSnapshot?: FrameworkSnapshot;
  deferUntilProvider?: boolean;
  router?: "browser" | "memory";
  initialPath?: string;
  basename?: string;
}

export function SiteApp({
  provider,
  initialSnapshot,
  deferUntilProvider = false,
  router = "browser",
  initialPath = "/",
  basename,
}: SiteAppProps) {
  const content = (
    <FrameworkProvider
      provider={provider}
      initialSnapshot={initialSnapshot}
      deferUntilProvider={deferUntilProvider}
    >
      <SiteRoutes />
    </FrameworkProvider>
  );
  if (router === "memory")
    return (
      <MemoryRouter basename={basename} initialEntries={[initialPath]}>
        {content}
      </MemoryRouter>
    );
  const configuredMountPath = document
    .querySelector<HTMLMetaElement>(
      `meta[name="${groundworksPublicMountMetaName}"]`,
    )
    ?.getAttribute("content");
  const browserBasename =
    basename ?? resolveGroundworksBasename(configuredMountPath);
  return <BrowserRouter basename={browserBasename}>{content}</BrowserRouter>;
}
